import { DeleteIcon, EditIcon, ExternalLinkIcon } from '@assets/icons';
import { CopyIconButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { useConfirmation, useModal } from '@components/modal';
import { Typography } from '@components/typography';
import { UMAP, useDeleteUMAPMutation } from '@generated/graphql';
import { Box, Grid } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import {
  DatasetsDetailsMicroserviceStatus,
  DatasetsDetailsUmapEditModal,
} from '@subsets/workspaces';

type DatasetsDetailsUmapListItemPreviewProps = {
  datasetId: string;
  umap: Partial<UMAP>;
  workspaceId: string;
  refetch: VoidFunction;
};

export const DatasetsDetailsUmapListItemPreview = ({
  datasetId,
  umap: { status, umapId, name, tags },
  workspaceId,
  refetch,
}: DatasetsDetailsUmapListItemPreviewProps) => {
  const { createAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [deleteUMAPMutation] = useDeleteUMAPMutation();
  const { organizationId } = useOrganizationContext();

  const { showModal: showEditModal } = useModal({
    component: DatasetsDetailsUmapEditModal,
    componentProps: {
      workspaceId,
      umapId,
      name,
      tags,
      refetch,
    },
    modalProps: { title: 'Edit UMAP' },
  });

  const handleDeleteUMAP = () =>
    showConfirmation({
      onAffirm: createAsyncNotification('Successfully deleted umap.', async () => {
        await deleteUMAPMutation({
          variables: {
            umapId,
            workspaceId,
          },
        });
        void refetch();
      }),
      message: `Are you sure you want to delete this umap?`,
    });

  return (
    <Grid container alignItems="center" justifyContent="flex-start">
      <Grid item xs={1.5}>
        <Box display="flex" marginLeft={1}>
          <DatasetsDetailsMicroserviceStatus
            status={status}
            workspaceId={workspaceId}
            microServiceName="umaps"
            datasetId={datasetId}
            microServiceId={umapId}
          />
        </Box>
      </Grid>
      <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
        <CopyIconButton value={umapId} showValue tooltipProps={{ variant: 'secondary' }} />
      </Grid>
      <Grid item xs={3}>
        <Typography variant="body2" handleOverFlow noWrap>
          {name || '-'}
        </Typography>
      </Grid>
      <Grid item lg={6}>
        <Group justifyContent="flex-end" gap={2}>
          {status !== 'failed' && (
            <>
              <InternalLink
                unstyled
                to={`/${organizationId}/workspaces/${workspaceId}/experiments/?tab=umaps&umapId=${umapId}`}
              >
                <IconButton Icon={ExternalLinkIcon} />
              </InternalLink>
              <IconButton onClick={() => showEditModal()} Icon={EditIcon} size={18} />
            </>
          )}
          <IconButton onClick={handleDeleteUMAP} Icon={DeleteIcon} />
        </Group>
      </Grid>
    </Grid>
  );
};
